/**
 * THE DOMAIN LENS — editorial policy held as DATA (narration review 2026-09-26,
 * Option D; founder 13:33 PT). A lens orders eligible findings, names the
 * references a so-what may compare against, lists the qualifications its domain
 * requires and the follow-ups it may offer. It is NOT an engine: amounts, ranks,
 * shares, signs and statuses stay with the engine, and materiality thresholds
 * stay in their governed policies (referenced by `policyRefs`, never copied).
 *
 * Selection is by the answer's governed dataset identity, never by question
 * text, tenant name or exhibit shape (`selectNarrativeLens`).
 */

/** The shared relations a narrative plan states; each has one reviewed microgrammar. */
export const ASK_PLAN_RELATIONS = [
  'figure',
  'part_of_whole',
  'change',
  'gap_to_reference',
  'largest',
  'status',
  'progress',
] as const;
export type AskPlanRelation = (typeof ASK_PLAN_RELATIONS)[number];

/** The typed references a so-what may compare against. */
export const ASK_REFERENCE_KINDS = [
  'budget',
  'target',
  'plan',
  'prior_period',
  'committed',
  'policy',
] as const;
export type AskReferenceKind = (typeof ASK_REFERENCE_KINDS)[number];

/**
 * What a plan fact asserts about its figure, typed by the engine's own facts
 * (sign of a change, the at-risk definition, a run-rate basis), never read from
 * label text. Qualifications key on these.
 */
export const ASK_PLAN_STATES = [
  'at_risk',
  'run_rate',
  'increase',
  'decrease',
  'below_reference',
  'realized',
  'validated',
  'plan_reduced',
] as const;
export type AskPlanState = (typeof ASK_PLAN_STATES)[number];

export type AskLensQualification = Readonly<{
  id: string;
  /** Reviewed reader sentence; a qualification never carries a number. */
  text: string;
  /** The plan states that make this qualification mandatory. */
  requiredWhen: readonly AskPlanState[];
}>;

export type AskLensFollowUp = Readonly<{
  id: string;
  label: string;
  /** Offered only when the plan's governing relation is this one. */
  relation: AskPlanRelation;
  /** And, when present, only when the governing fact states one of these. */
  whenStates?: readonly AskPlanState[];
}>;

export type AskNarrativeLens = Readonly<{
  lensId: string;
  version: number;
  owner: string;
  applicability: Readonly<{ datasetRefs: readonly string[] }>;
  /** Relations in the order this domain leads with. */
  priorities: readonly AskPlanRelation[];
  /** References a so-what may compare against, most preferred first. */
  comparators: readonly AskReferenceKind[];
  /** Governed materiality/threshold policies this lens defers to (by ref). */
  policyRefs: readonly string[];
  /** Domain terms, held for the grammar review; never used to route. */
  terminology: Readonly<Record<string, string>>;
  qualifications: readonly AskLensQualification[];
  followUps: readonly AskLensFollowUp[];
  /**
   * The advisory actions this domain may recommend (advisor voice, founder 2026-09-26 16:55),
   * each allowed only when the governing fact states one of `requiredWhen`. Never free text:
   * the sentence is the action's reviewed microgrammar.
   */
  advisoryActions?: readonly Readonly<{ id: string; requiredWhen: readonly AskPlanState[] }>[];
}>;

const RELATIONS: ReadonlySet<string> = new Set(ASK_PLAN_RELATIONS);
const REFERENCES: ReadonlySet<string> = new Set(ASK_REFERENCE_KINDS);
const STATES: ReadonlySet<string> = new Set(ASK_PLAN_STATES);
const DIGIT = /\d/;

/** Everything wrong with a lens, named; empty is the only loadable state. */
export function narrativeLensProblems(lens: AskNarrativeLens): readonly string[] {
  const problems: string[] = [];
  if (!lens.lensId || lens.version < 1 || !lens.owner) problems.push('lens identity incomplete');
  if (lens.applicability.datasetRefs.length === 0) problems.push('lens applies to no dataset');
  for (const relation of [...lens.priorities, ...lens.followUps.map((item) => item.relation)]) {
    if (!RELATIONS.has(relation)) problems.push(`unknown relation ${relation}`);
  }
  for (const kind of lens.comparators)
    if (!REFERENCES.has(kind)) problems.push(`unknown reference ${kind}`);
  for (const item of lens.qualifications) {
    if (DIGIT.test(item.text)) problems.push(`qualification ${item.id} carries a number`);
    for (const state of item.requiredWhen)
      if (!STATES.has(state)) problems.push(`unknown state ${state}`);
  }
  for (const action of lens.advisoryActions ?? []) {
    for (const state of action.requiredWhen)
      if (!STATES.has(state)) problems.push(`unknown state ${state}`);
  }
  for (const item of lens.followUps) {
    for (const state of item.whenStates ?? [])
      if (!STATES.has(state)) problems.push(`unknown state ${state}`);
    if (DIGIT.test(item.label)) problems.push(`follow-up ${item.id} carries a number`);
  }
  return problems;
}

/**
 * The one lens whose applicability covers EVERY governed dataset the answer
 * rests on. None, or more than one, is `null`: an answer spanning domains keeps
 * the plain grammar rather than borrowing one domain's editorial policy.
 */
export function selectNarrativeLens(
  lenses: readonly AskNarrativeLens[],
  datasetRefs: readonly string[]
): AskNarrativeLens | null {
  if (datasetRefs.length === 0) return null;
  const fits = lenses.filter((lens) => {
    const covered = new Set(lens.applicability.datasetRefs);
    return datasetRefs.every((ref) => covered.has(ref));
  });
  return fits.length === 1 ? fits[0] : null;
}
